import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { Skeleton } from 'components/ui/skeleton';
import { isLive, type InterventionRequest, type MissionCosts } from 'api/interventionRequestsApi';
import { parisDateTime } from 'utils/interventionDate';
import { activeLocale } from 'i18n';
import { byHtThenTtc, formatMoney, formatTotals } from 'utils/money';
import { missionTotals } from './missionCosts';
import { technicianName } from './upcomingAssignment';

/** A fee counts only while its technician can still do the mission (CA17). */
const countsIn = (requests: InterventionRequest[], technicianUserId: number | null): boolean =>
  requests.some((request) => request.technician.id === technicianUserId && isLive(request.status));

interface MissionCostCardProps {
  /** `undefined` while the read is in flight or failed — the drawer says the failure itself. */
  costs: MissionCosts | null | undefined;
  /** Every request of the mission, cancelled ones included: they name the technician of a fee. */
  requests: InterventionRequest[];
  isLoading: boolean;
}

/** F7/F8 — « Coût de la mission », in both drawers: one line per live operation, then the total. */
const MissionCostCard = ({ costs, requests, isLoading }: MissionCostCardProps) => {
  const { t, i18n } = useTranslation();
  if (!isLoading && !costs) return null;
  const locale = activeLocale(i18n.resolvedLanguage);
  const operations = costs ? [...(costs.tariff ? [costs.tariff] : []), ...(costs.fees ?? [])] : [];
  const totals = costs ? missionTotals({ ...costs, fees: costs.fees ?? [] }, requests) : null;
  const accepted = requests.some((request) => request.status === 'accepted');

  return (
    <section
      aria-labelledby="mission-cost-title"
      className="flex min-w-0 flex-col gap-3 rounded-md border border-zinc-200 p-4 dark:border-zinc-800"
    >
      <h3 id="mission-cost-title" className="text-[13px] font-semibold">
        {t('Mission cost')}
      </h3>
      {isLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-2/3" />
        </div>
      ) : operations.length === 0 ? (
        <p className="text-[12.5px] text-muted-foreground">{t('No cost entered')}</p>
      ) : (
        <>
          <ul className="flex flex-col gap-2">
            {operations.map((operation) => {
              const amount = formatMoney(operation.amountCents, operation.taxBasis, locale);
              const request = requests.find((candidate) => candidate.technician.id === operation.technicianUserId);
              const counted = operation.type === 'tariff' || countsIn(requests, operation.technicianUserId);
              return (
                <li
                  key={operation.id}
                  className={cn('flex flex-col gap-0.5 text-[12.5px]', !counted && 'text-muted-foreground opacity-70')}
                >
                  <span className="font-medium">
                    {operation.type === 'tariff' ? t('Tariff: {{amount}}', { amount }) : t('Travel fee: {{amount}}', { amount })}
                    {request && ` · ${technicianName(request)}`}
                  </span>
                  {operation.note && <span className="whitespace-pre-line">{operation.note}</span>}
                  {operation.createdByUsername && (
                    <span className="text-[12px] text-muted-foreground">
                      {t('by {{ops}} on {{date}}', {
                        ops: operation.createdByUsername,
                        date: parisDateTime(operation.createdAt).date,
                      })}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
          {totals && (
            <p className="border-t pt-2 text-[13px] font-semibold">
              {accepted || byHtThenTtc(totals.min, totals.max) === 0
                ? t('Total · {{amount}}', { amount: formatTotals(totals.max, locale) })
                : t('Total depending on the retained technician: {{min}} to {{max}}', {
                    min: formatTotals(totals.min, locale),
                    max: formatTotals(totals.max, locale),
                  })}
            </p>
          )}
        </>
      )}
    </section>
  );
};

export default MissionCostCard;
